import { type FunctionComponent } from 'react';
import {
  type ToastMessageProps,
  TOAST_STATUS,
} from '@leagueplatform/toast-messages';
import { FormattedHTMLMessage } from '@leagueplatform/locales';
import {
  type GDSStatus,
  StackLayout,
  StackItem,
  StatusBanner,
  HeadingText,
} from '@leagueplatform/genesis-core';
import type { LocalesKey } from 'types/types';

const StatusBannerMap: Record<TOAST_STATUS, GDSStatus> = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  note: 'neutral',
  attention: 'warning',
  info: 'info',
  inverted: 'brand',
};

export const ToastMessage: FunctionComponent<ToastMessageProps> = ({
  type,
  dismiss,
  messageId,
  textId,
  text,
  contentId,
  content,
  values,
  toastMessageMouseEntered,
  toastMessageMouseLeft,
}) => (
  // Right-align the toast and cap its width on desktop (per Figma); `width: 100%`
  // + `maxWidth` lets it fill the screen on mobile and shrink to the capped width
  // on larger viewports, where `horizontalAlignment="end"` pins it to the right.
  <StackLayout orientation="horizontal" horizontalAlignment="end">
    <StackItem
      css={{ width: '100%', maxWidth: '28rem', pointerEvents: 'auto' }}
      // Pause-on-hover: the toast manager wires these to start/stop the
      // auto-dismiss timer, so the toast stays put while the user reads it or
      // reaches for Dismiss. Attached to the visible toast box (the element with
      // pointer-events: auto), not the full-width wrapper.
      onMouseEnter={toastMessageMouseEntered}
      onMouseLeave={toastMessageMouseLeft}
    >
      <StatusBanner
        status={StatusBannerMap[type]}
        title={
          textId || text ? (
            <HeadingText level="display" size="sm">
              {/* This is the renderer for every toast in the app, so it handles
                  all four Toast copy fields: a message id (textId/contentId) or
                  pre-formatted text (text/content). FormattedHTMLMessage needs a
                  LocalesKey; the id cast is safe at this boundary — the id is an
                  app STR_ key or an SDK message key resolved by the i18n provider. */}
              {textId ? (
                <FormattedHTMLMessage
                  id={textId as LocalesKey}
                  values={values}
                />
              ) : (
                text
              )}
            </HeadingText>
          ) : null
        }
        onDismissClick={() => dismiss(messageId)}
        aria-live="polite"
      >
        {contentId ? (
          <FormattedHTMLMessage id={contentId as LocalesKey} values={values} />
        ) : (
          content
        )}
      </StatusBanner>
    </StackItem>
  </StackLayout>
);
